import { classNames } from "Utils/utils";
import { INJURY_CAUSES } from "../../constants";
import { backRegions, frontRegions } from "../../data/Coordinates";
import HumanBodyWrapper from "../HumanBodyWrapper";
import { YesNoToggle } from "../forms/YesNoToggle";
import useCareLabels from "hooks/useCareLabels";

export const InjuryFormStep = ({
  selectedInjuryCauses,
  setSelectedInjuryCauses,
  injuryCauseOther,
  setInjuryCauseOther,
  selectedBodyParts,
  handleBodyPartChange,
  register,
  watch,
  setValue,
  errors,
}) => {
  const { participantLabel } = useCareLabels();
  const hasOtherCause = selectedInjuryCauses.some((c) => c.value === "other");
  const abuseCheckValue = watch("abuse_check");
  const treatmentAdministeredValue = watch("treatment_administered");
  const allRegions = [...frontRegions, ...backRegions];

  return (
    <div className="animate-fadeIn space-y-6">
      {/* Header */}

      {/* Top Row: Integrated Selection (60/40 Split) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
        {/* Left Side (60%): Body Diagram */}
        <div className="lg:col-span-3">
          <label className="block text-sm font-medium text-gray-900 mb-3">
            Location on body (please circle and specify):
          </label>
          <HumanBodyWrapper
            selectedParts={selectedBodyParts}
            onPartsChange={handleBodyPartChange}
            error={selectedBodyParts.length === 0 && errors.body_parts}
            compact
          />
        </div>

        {/* Right Side (40%): Selected Body Parts Chips */}
        <div className="lg:col-span-2">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Selected Body Parts <span className="text-red-500">*</span>
          </h3>
          {selectedBodyParts.length > 0 ? (
            <div
              className="space-y-2 pr-4  max-h-96 overflow-y-auto scrollbar-thin"
              style={{
                scrollbarColor: "#38528A transparent",
                scrollbarWidth: "thin",
              }}
            >
              {selectedBodyParts.map((partId) => {
                const region = allRegions.find((r) => r.id === partId);
                if (!region) return null;

                return (
                  <div
                    key={partId}
                    className="flex items-center justify-between bg-white border border-blue-300 rounded-lg px-3 py-2 shadow-sm hover:border-blue-400 transition-colors"
                  >
                    <span className="text-sm font-medium text-blue-800 flex-1">
                      {region.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        const newParts = selectedBodyParts.filter(
                          (id) => id !== partId
                        );
                        handleBodyPartChange(newParts);
                      }}
                      className="ml-2 text-red-500 hover:text-red-700 transition-colors"
                      aria-label={`Remove ${region.name}`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic text-center py-8 bg-white border border-gray-200 rounded-lg">
              No body parts selected yet. Click on the diagram to indicate
              injury locations.
            </p>
          )}
        </div>
      </div>

      {/* Integrated Form Body - Continuous Flow */}
      <div className="space-y-6">
        {/* Cause of Injury - 3-Column Grid */}
        <section className="p-6">
          <h3 className="text-base font-bold text-gray-900 mb-4">
            Cause of Injury
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {INJURY_CAUSES.map((cause) => {
              const isSelected = selectedInjuryCauses.some(
                (c) => c.value === cause.value
              );
              return (
                <label
                  key={cause.value}
                  className={classNames(
                    "flex items-start gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm",
                    isSelected
                      ? "border-blue-500 bg-blue-50 text-blue-700"
                      : "border-gray-200 hover:border-gray-300 text-gray-700"
                  )}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedInjuryCauses([
                          ...selectedInjuryCauses,
                          cause,
                        ]);
                      } else {
                        setSelectedInjuryCauses(
                          selectedInjuryCauses.filter(
                            (c) => c.value !== cause.value
                          )
                        );
                        if (cause.value === "other") {
                          setInjuryCauseOther("");
                        }
                      }
                    }}
                    className="w-4 h-4 text-blue-600 rounded mt-0.5 flex-shrink-0"
                  />
                  <span className="font-medium text-sm">{cause.label}</span>
                </label>
              );
            })}
          </div>

          {/* Conditional: Nature of Injury (if Other selected) - Full Width */}
          {hasOtherCause && (
            <div className="mt-4 animate-fadeIn">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Nature of injury (Link to other):
              </label>
              <textarea
                value={injuryCauseOther}
                onChange={(e) => {
                  setInjuryCauseOther(e.target.value);
                  setValue("injury_cause_other", e.target.value);
                }}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
                placeholder="Specify the nature of the injury..."
              />
            </div>
          )}

          {/* Specify Field - Additional details about injury location */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              Specify (additional details about injury location):
            </label>
            <textarea
              {...register("injury_details")}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
              placeholder="Provide additional details about the injury..."
            />
          </div>
        </section>

        {/* Compliance & Abuse Check - Same Row Horizontal Layout */}
        <section className="p-6 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Abuse Check Question */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Does the {participantLabel} look like there has been Abuse/ Neglect/
                Exploitation/ Discrimination:{" "}
                <span className="text-gray-500 font-normal">
                  (If yes, provide details below)
                </span>
              </label>
              <div className="mb-3">
                <YesNoToggle
                  name="abuse_check"
                  value={abuseCheckValue}
                  onChange={(val) => {
                    setValue("abuse_check", val);
                    if (val !== "yes") setValue("abuse_details", "");
                  }}
                  showNA={true}
                />
              </div>

              {/* Conditional: Abuse Details (if Yes) */}
              {abuseCheckValue === "yes" && (
                <div className="mt-3 animate-fadeIn">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Details: <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    {...register("abuse_details", {
                      required:
                        abuseCheckValue === "yes"
                          ? "Details are required when Abuse/Neglect is indicated"
                          : false,
                    })}
                    rows={3}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm resize-y"
                    placeholder="Provide detailed information about the abuse, neglect, exploitation, or discrimination..."
                  />
                  {errors.abuse_details && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.abuse_details.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Treatment Administered Question */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-3">
                Treatment Administered:{" "}
                <span className="text-gray-500 font-normal">
                  (If yes, provide by whom – Name & Role)
                </span>
              </label>
              <div className="mb-3">
                <YesNoToggle
                  name="treatment_administered"
                  value={treatmentAdministeredValue}
                  onChange={(val) => {
                    setValue("treatment_administered", val);
                    if (val !== "yes") setValue("treatment_administrator", "");
                  }}
                  showNA={true}
                />
              </div>

              {/* Conditional: Treatment Administrator (if Yes) */}
              {treatmentAdministeredValue === "yes" && (
                <div className="mt-3 animate-fadeIn">
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    By whom (Name & Role):
                  </label>
                  <input
                    type="text"
                    {...register("treatment_administrator")}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter name and role of the person who administered treatment..."
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};
